<template>
  <div>
    <div class="f f-as f-gap-16">
      <div
        v-for="(column, idx) in columns"
        :key="`col-${idx}`"
        :ref="(el) => columnEls[idx] = el"
        class="f-v f-gap-16"
      >
        <shoutouts-profiles-wall-card
          v-for="(shoutout, rIdx) in column"
          :key="`shoutout-${rIdx}`"
          :shoutout="shoutout"
          :program="program"
          :is-for-current-user="!userId"
        />
      </div>
    </div>
    <donut-loading-dots v-if="wallIsFetching" class="f-jc m-tx1" />
    <div v-if="showEmptyState" class="t">You have not received any {{ $i18n.t('shoutouts.shoutout', { count: 2 }) }} yet! Once you receive them, they will appear here.</div>
  </div>
</template>

<script setup>
  import Network from 'lib/api/network';
  import { ref, reactive, computed, inject, onMounted, onUnmounted, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import useClientState from 'composables/client-state';
  import useInfiniteScroll from 'composables/infinite-scroll';

  const { dispatch } = useStore();
  const $i18n = inject('$i18n');
  const { getQueryParam } = useClientState();

  const wallIsFetching = ref(false);
  const program = ref(null);
  const columns = reactive([[], [], []]);
  const columnEls = reactive([null, null, null]);
  const showEmptyState = computed(() => !wallIsFetching.value && !columns.some(column => column.length));

  // Shoutouts Processing
  const assignIntoShortestColumn = (shoutout) => {
    let shortestIdx = 0;
    columnEls.forEach((columnEl, idx) => {
      if (!columnEl || !columnEls[shortestIdx]) return;
      if (columnEl.scrollHeight < columnEls[shortestIdx].scrollHeight) {
        shortestIdx = idx;
      }
    });
    columns[shortestIdx].push(shoutout);
  };
  const assignIntoColumns = (shoutouts) => {
    // This method recursively assigns shoutouts into columns based on their height. It's recursive
    // such that we can do nextTicks in between each iteration, which we need to do to check heights.
    let assigningIdx = 0;
    const assignNext = () => {
      if (assigningIdx < shoutouts.length) {
        nextTick(() => {
          assignIntoShortestColumn(shoutouts[assigningIdx]);
          assigningIdx += 1;
          assignNext();
        });
      }
    };
    assignNext();
  };

  // Data Fetching
  const userId = getQueryParam('user_id');
  const nextPage = ref(1);
  const fetchWall = () => new Promise((resolve) => {
    wallIsFetching.value = true;
    let url = `/shoutouts/profile/wall?page=${nextPage.value}`;
    if (userId) url += `&user_id=${userId}`;
    Network.get(url, {
      success: (data) => {
        window.donut.track('Shoutouts Wall — Fetched Page', { pageNum: nextPage.value });
        assignIntoColumns(data.shoutouts.current_page);
        program.value = data.program;
        nextPage.value = data.shoutouts.next_page;
        resolve();
      },
      error: () => {
        dispatch('setErrorToast');
        window.donut.track('Shoutouts Wall — Fetch Error', { pageNum: nextPage.value });
      },
      complete: () => {
        wallIsFetching.value = false;
      },
    });
  });

  // Infinite Scroll
  const shouldFetchAtEndOfScroll = computed(() => nextPage.value && !wallIsFetching.value);
  useInfiniteScroll({ fetchData: fetchWall, shouldFetchAtEndOfScroll });
</script>
